import { Briefcase, CalendarDays, ClipboardList, Loader2 } from 'lucide-react';
import { useGetInvestigationAgentBriefQuery } from '@/store/api/apiSlice';

interface AgentBriefCardProps {
  investigationId: string;
  // Compact form drops the section overview and shrinks padding — used inside
  // the checklist panel header where the page already carries its own chrome.
  compact?: boolean;
  className?: string;
}

// Renders the per-investigation "Scope of Work" so the agent knows exactly
// what tier they're working and what's expected before they open the checklist.
export default function AgentBriefCard({
  investigationId,
  compact = false,
  className = '',
}: AgentBriefCardProps) {
  const { data, isLoading, isError } = useGetInvestigationAgentBriefQuery(investigationId);

  if (isLoading) {
    return (
      <div className={`rounded-lg border border-[#f0f0f0] bg-white px-4 py-3 text-xs text-[#717680] flex items-center gap-2 ${className}`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading scope of work…
      </div>
    );
  }

  if (isError || !data) return null;

  const { brief, checklistOverview } = data;

  return (
    <div className={`rounded-lg border border-[#FF6B35]/20 bg-[#FFF8F4] ${compact ? 'px-4 py-3' : 'px-5 py-4'} ${className}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#FF6B35]">
              Scope of work
            </span>
            <span className="text-sm font-semibold text-[#1b1b1b]">{brief.label}</span>
          </div>
          <p className={`${compact ? 'text-xs' : 'text-sm'} text-[#4e4d4d] leading-relaxed mt-1`}>
            {brief.summary}
          </p>

          <div className="flex items-center gap-3 flex-wrap mt-2 text-[11px] text-[#717680]">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" />
              {brief.workHistoryYears}y work history
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" />
              ~{brief.estimatedDays} day{brief.estimatedDays === 1 ? '' : 's'} to complete
            </span>
            <span className="inline-flex items-center gap-1">
              <ClipboardList className="w-3.5 h-3.5" />
              {checklistOverview.totalItems} checklist items across {checklistOverview.sectionCount} sections
            </span>
          </div>

          {!compact && brief.scope.length > 0 && (
            <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 list-disc list-inside text-xs text-[#4e4d4d]">
              {brief.scope.map((item, i) => (
                <li key={i} className="leading-snug">
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
